import React, { useEffect, useMemo, useState } from "react";
import CustomBreadcrumb from "../../common/CustomBreadcrumb";
import CommonTable from "../../common/CommonTable";
import { BreadCrum } from "../../common";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAiCampaignDetailsHandler } from "../../redux/action/getAiCampaignDetailsSlice";
import { getAiCampaignMembersHandler } from "../../redux/action/getAiCampaignMembersSlice";
import { getMemberActionStatusesHandler } from "../../redux/action/getMemberActionStatusesSlice";
import {
  updateAiCampaignStatusHandler,
  updateAiCampaignStatusAction,
} from "../../redux/action/updateAiCampaignStatusSlice";
import {
  exportAiCampaignResultsHandler,
  exportAiCampaignResultsAction,
} from "../../redux/action/exportAiCampaignResultsSlice";
import { restartAiCampaignAction } from "../../redux/action/restartAiCampaignSlice";
import { Checkbox, message } from "antd";
import Loader from "../Loder/loader";
import "./ManageCampaign.css";
import { EditIcon, PauseIcon, Trash, InfoIcon } from "../../assets/icon";
import CampaignModal from "../../common/CampaignModal";
import { normalizeCampaignStatusKey } from "./aiCampaignsUtils";
import { LEAD_DETAILS_COLUMNS } from "./manageCampaignConstants";
import ManageCampaignToolbar from "./ManageCampaignToolbar";
import ManageCampaignDetailsTab from "./ManageCampaignDetailsTab";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";

function triggerExportReportDownload(fileUrl) {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = "export-campaign-report.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const ManageCampaign = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { canEditCampaigns } = useAdminPermissions();
  const isViewOnly = !canEditCampaigns;
  const [activeTab, setActiveTab] = useState("campaignDetails");
  const { campaignId, refBrandId } = useLocation().state || {};
  const [statusValue, setStatusValue] = useState(undefined);

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);

  const [exportOptions, setExportOptions] = useState({
    censusData: true,
    latestStatus: true,
    decision: true,
    decisionDateTime: true,
  });

  const handleExportCheckboxChange = (key) => {
    setExportOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const campaignDetailsState = useSelector(
    (state) => state?.getAiCampaignDetails,
  );


  const campaignData =
    campaignDetailsState?.data?.data ?? campaignDetailsState?.data;
  const campaignStatusKey = normalizeCampaignStatusKey(campaignData?.status);
  const isHold = campaignStatusKey === "hold";
  const isArchived = campaignStatusKey === "archive";

  const updateStatusState = useSelector(
    (state) => state?.updateAiCampaignStatus,
  );
  const exportResultsState = useSelector(
    (state) => state?.exportAiCampaignResults,
  );
  const campaignMembersState = useSelector(
    (state) => state?.getAiCampaignMembers,
  );
  const memberActionStatusesState = useSelector(
    (state) => state?.getMemberActionStatuses,
  );
  const leadStatusOptions = useMemo(() => {
    const res = memberActionStatusesState?.data;
    if (!res) return [];
    const raw = Array.isArray(res.data) ? res.data : res.data?.data;
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => {
      if (typeof item === "string") return { value: item, label: item };
      const value = item.value ?? item.code ?? item.status ?? "";
      const label = item.label ?? item.name ?? value;
      return { value: value || label, label };
    });
  }, [memberActionStatusesState?.data]);
  const restartCampaignState = useSelector((state) => state?.restartAiCampaign);

  const [leadDetails, setLeadDetails] = useState([]);
  const [searchText,  setSearchText] = useState("");
  const [leadPagination, setLeadPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: [5, 10, 15, 20],
    className: "order-pagination",
    locale: { items_per_page: "" },
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
  });
  const { current: leadCurrentPage, pageSize: leadPageSize } = leadPagination;

  useEffect(() => {
    if (campaignId && refBrandId) {
      dispatch(getAiCampaignDetailsHandler({ campaignId, refBrandId }));
    }
  }, [campaignId, refBrandId, dispatch]);

  useEffect(() => {
    if (!campaignId || !refBrandId || activeTab !== "leadDetails") return;
    dispatch(
      getAiCampaignMembersHandler({
        campaignId,
        refBrandId,
        search: searchText,
        offset: leadCurrentPage,
        limit: leadPageSize,
        ...(Array.isArray(statusValue) && statusValue.length > 0
          ? { memberActionStatus: statusValue }
          : {}),
      }),
    );
  }, [
    activeTab,
    campaignId,
    dispatch,
    leadCurrentPage,
    leadPageSize,
    refBrandId,
    searchText,
    statusValue,
  ]);

  useEffect(() => {
    const response = campaignMembersState?.data;
    const items = response?.data ?? response?.members ?? [];
    if (!Array.isArray(items)) return;

    const mappedMembers = items.map((item, index) => {
      const firstName = item?.first_name ?? item?.firstName ?? "";
      const lastName = item?.last_name ?? item?.lastName ?? "";
      const fullName = `${firstName} ${lastName}`.trim();

      return {
        key: item?.id ?? item?.memberId ?? index,
        name: fullName || item?.name || item?.leadName || "-",
        email: item?.email ?? item?.leadEmail ?? "-",
        phone: item?.phone ?? item?.phoneNumber ?? "-",
        status: item?.outreachStatus ?? item?.outreachStatus ?? "-",
        ...item,
      };
    });

    setLeadDetails(mappedMembers);
    setLeadPagination((prev) => ({
      ...prev,
      total: response?.totalElements ?? response?.total ?? prev.total,
      // Keep `current` from client state — it matches the page we requested via
      // `offSet` in getAiCampaignMembersHandler. API metadata (pageNumber/offSet)
      // can be missing or use different semantics and was resetting the pager.
      pageSize: response?.pageSize ?? response?.limit ?? prev.pageSize,
    }));
  }, [campaignMembersState?.data]);

  useEffect(() => {
    if (updateStatusState?.message && updateStatusState?.data) {
      message.success(updateStatusState.message);
      dispatch(updateAiCampaignStatusAction.updateAiCampaignStatusInfoReset());
      dispatch(getAiCampaignDetailsHandler({ campaignId, refBrandId }));
    }
    if (updateStatusState?.error) {
      message.error(updateStatusState.error);
      dispatch(updateAiCampaignStatusAction.updateAiCampaignStatusInfoReset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updateStatusState?.message,
    updateStatusState?.error,
    updateStatusState?.data,
  ]);

  useEffect(() => {
    if (restartCampaignState?.message && restartCampaignState?.data) {
      message.success(restartCampaignState.message);
      dispatch(restartAiCampaignAction.restartReset());
      dispatch(getAiCampaignDetailsHandler({ campaignId, refBrandId }));
    }
    if (restartCampaignState?.error) {
      message.error(restartCampaignState.error);
      dispatch(restartAiCampaignAction.restartReset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    restartCampaignState?.message,
    restartCampaignState?.error,
    restartCampaignState?.data,
  ]);

  useEffect(() => {
    const payload = exportResultsState?.data?.data;
    const fileUrl = payload?.reportUrl;
    if (!payload) return;
    triggerExportReportDownload(fileUrl);

    if (exportResultsState?.message) {
      message.success(exportResultsState.message);
    }
    setShowExportModal(false);
    dispatch(exportAiCampaignResultsAction.exportAiCampaignResultsInfoReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportResultsState?.data]);

  useEffect(() => {
    if (!exportResultsState?.error) return;
    message.error(exportResultsState.error);
    dispatch(exportAiCampaignResultsAction.exportAiCampaignResultsInfoReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportResultsState?.error]);

  const handleStatusUpdate = (status) => {
    dispatch(updateAiCampaignStatusHandler({ campaignId, status, refBrandId }));
  };

  const handleEditCampaign = () => {
    if (campaignId == null || refBrandId == null) return;
    navigate("/ai-campaigns/campaign-details", {
      state: { mode: "edit", campaignId, refBrandId },
    });
  };

  const handleExportResults = () => {
    if (!campaignId || !refBrandId) {
      message.error("Campaign details are missing");
      return;
    }
    if (exportResultsState?.isLoading) return;
    dispatch(
      exportAiCampaignResultsHandler({
        campaignId,
        refBrandId,
        // exportOptions: {
        //   censusData: exportOptions.censusData,
        //   latestStatus: exportOptions.latestStatus,
        //   decision: exportOptions.decision,
        //   decisionDateTime: exportOptions.decisionDateTime,
        // },
      }),
    );
  };

  const handleLeadPageChange = (page, pageSize) => {
    setLeadPagination((prev) => ({
      ...prev,
      current: page,
      ...(pageSize != null ? { pageSize } : {}),
    }));
  };

  const handleLeadSearch = (value) => {
    setSearchText(value);
    setLeadPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  };

  const getReadableCsvFileName = (fileName) => {
    if (!fileName) return "No file uploaded";
    try {
      return decodeURIComponent(fileName);
    } catch (error) {
      return fileName;
    }
  };

  const handleDownloadCsvFile = () => {
    const fileUrl = campaignData?.csvFileUrl;
    if (!fileUrl) {
      message.error("CSV file URL is not available");
      return;
    }

    const link = document.createElement("a");
    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = getReadableCsvFileName(
      campaignData?.csvFileName || "census.csv",
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {campaignDetailsState?.isLoading || updateStatusState?.isLoading ? (
        <Loader loaderTransform="loaderTransform" />
      ) : (
        <div className="rightside">
          <div className="rightcontent">
            <div className="d-flex justify-content-between align-items-center mb-3 responsiveToolTabWrapper">
              <div
                className="d-flex flex-column gap-2"
                style={{ marginBottom: "-40px" }}
              >
                <p className="mb-0">
                  <strong>Campaign</strong>
                </p>
                <CustomBreadcrumb
                  items={[
                    { text: BreadCrum.HOME },
                    {
                      text: "AI Campaigns",
                      active: false,
                    },
                    {
                      text: "Campaign List",
                      onClick: () => navigate("/ai-campaigns/campaign-list"),
                      active: true,
                    },
                    {
                      text: isViewOnly ? "View Campaign" : "Manage Campaign",
                      active: true,
                    },
                  ]}
                />
              </div>
              {campaignData?.campaignType === "ENROLLMENT" && (
                <ManageCampaignToolbar
                  viewOnly={isViewOnly}
                  isHold={isHold}
                  isArchived={isArchived}
                  statusKey={campaignStatusKey}
                  statusLoading={updateStatusState?.isLoading}
                  onRestart={() =>
                    navigate("/ai-campaigns/manage-material/restart", {
                      state: { campaignId, refBrandId },
                    })
                  }
                  onHold={() => setShowHoldModal(true)}
                  onArchive={() => setShowArchiveModal(true)}
                  onExport={() => setShowExportModal(true)}
                />
              )}
            </div>

            <div className="manage-campaign-tabs d-flex justify-content-between align-items-center mt-4">
              <div className="d-flex gap-4">
                <div
                  className={`cursor-pointer manage-campaign-tab fs-16 ${activeTab === "campaignDetails" ? "active" : ""}`}
                  onClick={() => setActiveTab("campaignDetails")}
                >
                  Campaign Details
                </div>
                <div
                  className={`cursor-pointer manage-campaign-tab fs-16 ${activeTab === "leadDetails" ? "active" : ""}`}
                  onClick={() => setActiveTab("leadDetails")}
                >
                  Lead Details
                </div>
              </div>
              {campaignData?.campaignType === "ENROLLMENT" &&
                campaignData?.status === "DRAFT" &&
                !isViewOnly && (
                <div
                  className="d-flex align-items-center cursor-pointer fw-bold pb-2 manage-campaign-edit gap-2 fs-18"
                  onClick={handleEditCampaign}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleEditCampaign();
                    }
                  }}
                >
                  <EditIcon />
                  <span>Edit</span>
                </div>
              )}
            </div>

            {activeTab === "campaignDetails" && (
              <ManageCampaignDetailsTab
                campaignData={campaignData}
                detailsLoading={campaignDetailsState?.isLoading}
                getReadableCsvFileName={getReadableCsvFileName}
                onDownloadCsv={handleDownloadCsvFile}
              />
            )}
            {activeTab === "leadDetails" && (
              <div className="usercard mb-30">
                <div className="campaignDetailsCard">
                  <span className="campaigncardHeader ">Lead Details</span>
                </div>
                <hr className="m-0  manage-campaign-divider" />
                <div className="campaigncardBody d-flex flex-column gap-0 p-0">
                  <div className="table-responsive">
                    <CommonTable
                      body={leadDetails}
                      columns={LEAD_DETAILS_COLUMNS}
                      handlePageChange={handleLeadPageChange}
                      pagination={leadPagination}
                      tableLoading={campaignMembersState?.isLoading}
                      showSearch
                      searchValue={searchText}
                      onSearchChange={handleLeadSearch}
                      showStatusFilter
                      statusOptions={leadStatusOptions}
                      statusOptionsLoading={
                        memberActionStatusesState?.isLoading
                      }
                      onStatusFilterOpen={() => {
                        if (memberActionStatusesState?.isLoading) return;
                        if (leadStatusOptions.length > 0) return;
                        dispatch(getMemberActionStatusesHandler());
                      }}
                      statusValue={statusValue}
                      onStatusChange={setStatusValue}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <CampaignModal
            open={showArchiveModal}
            onCancel={() => setShowArchiveModal(false)}
            onAction={() => {
              handleStatusUpdate("ARCHIVE");
              setShowArchiveModal(false);
            }}
            title="Archive Campaign"
            subtitle="Move campaign to archive and preserve data"
            actionLabel="Archive Campaign"
            actionIcon={<Trash color={"#fff"} />}
            actionDisabled={updateStatusState?.isLoading}
          >
            <div className="campaign-modal-info-box">
              <div className="d-flex align-items-center gap-2 mb-2">
                <InfoIcon />
                <strong className="campaign-modal-info-title">
                  Archiving this campaign will:
                </strong>
              </div>
              <div>
                Are you sure you want to archive this campaign? This will pause all activity and move the campaign to archive.
              </div>
              {/* <ul className="campaign-modal-list">
                <li>Post the Campaign date (moved to Archive)</li>
                <li>Move status from Active/Draft/Hold to Archive</li>
                <li>Turn off all activities from AI Agent</li>
                <li>Retain all data and progress for perspective members</li>
                <li>
                  Not allowed to restart or edit the campaign (read only mode)
                </li>
              </ul> */}
            </div>
          </CampaignModal>

          <CampaignModal
            open={showExportModal}
            onCancel={() => setShowExportModal(false)}
            onAction={handleExportResults}
            title="Export Results"
            subtitle="Export campaign data at any given point of time"
            actionLabel={
              exportResultsState?.isLoading
                ? "Exporting..."
                : "Export Results"
            }
            actionIcon={
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z"
                  fill="#fff"
                />
              </svg>
            }
          >
            <div className="campaign-modal-info-box">
              <div className="d-flex align-items-center gap-2 mb-2">
                <InfoIcon />
                <strong className="campaign-modal-info-title">
                  This action will:
                </strong>
              </div>
              <p className="campaign-modal-info-text mb-0">
                This will export the campaign data provide lead data, outreach status, and decision data.
              </p>
            </div>
            {/* <div className="campaign-modal-checkbox-list">
              <div className="campaign-modal-checkbox-item">
                <Checkbox
                  checked={exportOptions.censusData}
                  onChange={() => handleExportCheckboxChange("censusData")}
                  className="campaign-modal-checkbox"

                >
                  Census Data
                </Checkbox>
              </div>
              <div className="campaign-modal-checkbox-item">
                <Checkbox
                  checked={exportOptions.latestStatus}
                  onChange={() => handleExportCheckboxChange("latestStatus")}
                  className="campaign-modal-checkbox"
                >
                  Latest Status
                </Checkbox>
              </div>
              <div className="campaign-modal-checkbox-item">
                <Checkbox
                  checked={exportOptions.decision}
                  onChange={() => handleExportCheckboxChange("decision")}
                  className="campaign-modal-checkbox"
                >
                  Decision
                </Checkbox>
              </div>
              <div className="campaign-modal-checkbox-item">
                <Checkbox
                  checked={exportOptions.decisionDateTime}
                  onChange={() =>
                    handleExportCheckboxChange("decisionDateTime")
                  }
                  className="campaign-modal-checkbox"
                >
                  Decision Date/Time Stamp
                </Checkbox>
              </div>
            </div>
            <div className="campaign-modal-note-box">
              <div className="d-flex align-items-start gap-2">
                <div className="mt-1">
                  <InfoIcon />
                </div>
                <span>
                  Once the campaign closes on the morning after the last date of
                  the campaign (7:00am central US time) &quot;Campaign End
                  Date&quot; the entire results set should be automatically
                  emailed to pre-defined physical users at
                </span>
              </div>
            </div> */}
          </CampaignModal>

          <CampaignModal
            open={showHoldModal}
            onCancel={() => setShowHoldModal(false)}
            onAction={() => {
              handleStatusUpdate("HOLD");
              setShowHoldModal(false);
            }}
            title="Hold Campaign"
            subtitle="Pause all communication from AI Agent for this campaign"
            actionLabel="Hold Campaign"
            actionIcon={<PauseIcon color={"#fff"} />}
            actionDisabled={updateStatusState?.isLoading}
          >
            <div className="campaign-modal-info-box">
              <div className="d-flex align-items-center gap-2 mb-2">
                <InfoIcon />
                <strong className="campaign-modal-info-title">
                  This action will:
                </strong>
              </div>
              <p className="campaign-modal-info-text mb-0">
                Pause all communication from AI Agent Stop email, SMS, and phone
                calls. You can restart the campaign later
              </p>
            </div>
          </CampaignModal>
        </div>
      )}
    </>
  );
};

export default ManageCampaign;
